import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import axios from 'axios';
import isPast from 'date-fns/is_past';
import isFuture from 'date-fns/is_future';

import {
  Link,
  Button,
  Switch,
  Status,
  Preloader,
  Table,
  I18n,
} from 'components';
import { formatDate, formatDateString } from 'utils/formatString';
import styles from './styles.pcss';
import { policeShape } from '../../shapes';

const cx = classNames.bind(styles);

export class InsurancePolicy extends Component {
  constructor(props) {
    super(props);

    this.state = {
      requestId: null,
      error: null,
      isDisableDialogVisible: false,
      isDisabling: false,
      isPeriodsTableVisible: false,
    };
  }

  toggleDisableDialog = () => this.setState((prevState) => ({
    isDisableDialogVisible: !prevState.isDisableDialogVisible,
    error: null,
  }));

  disable = async () => {
    const { presetId, splId } = this.props;

    this.setState({
      isDisabling: true,
      isDisableDialogVisible: false,
    });

    try {
      const { data } = await axios.post('/api/profile/insurance/disable/', {
        serviceId: presetId,
        splId,
      });

      if (data.requestId) {
        this.setState({
          requestId: data.requestId,
          isDisabling: false,
        });
      }

      if (data.isProcessing) {
        this.setState({
          error: {
            message: `Мы еще обрабатываем ваш предыдущий запрос.
             Пожалуйста, дождитесь его исполнения и повторите попытку`,
          },
          isDisabling: false,
        });
      }

      if (!data.requestId && !data.isProcessing) {
        this.setState({
          error: {
            message: 'При выполнении операции произошла ошибка. Попробуйте еще раз',
          },
          isDisabling: false,
        });
      }
    } catch (error) {
      this.setState({
        error: {
          message: 'При выполнении операции произошла ошибка. Попробуйте еще раз',
        },
        isDisabling: false,
      });
    }
  }

  render() {
    const {
      serviceName,
      presetUrl,
      shortDescription,
      insuranceSum,
      number,
      downloadUrl,
      subscriptionFee,
      lastPeriodPayed: lastPayedPeriodEnd,
      periods,
      isEnabled,
    } = this.props;
    const {
      error,
      requestId,
      isDisableDialogVisible,
      isDisabling,
      isPeriodsTableVisible,
    } = this.state;

    const currentPeriod = periods.find((p) => isFuture(p.datePeriodEnd) && isPast(p.datePeriodStart));
    const lastPayedPeriod = periods.find((p) => p.datePeriodEnd === lastPayedPeriodEnd);

    return (
      <div className={cx('component')}>
        <div className={cx('switch')}>
          <Switch
            checked={
              isEnabled
              && !isDisabling
              && !isDisableDialogVisible
              && !requestId
            }
            disabled={
              !isEnabled
              || isDisabling
              || requestId
            }
            onChange={this.toggleDisableDialog}
            isLoading={Boolean(isDisabling || requestId)}
          />
        </div>

        <div className={cx('content')}>
          <Link href={presetUrl}>
            {serviceName}
          </Link>

          {isDisableDialogVisible && (
            <>
              <Status
                className={['attention', cx('status')]}
                emoji="statusFail"
              >
                <I18n path="insurance.disableDialogAttention" />
              </Status>

              <Status className={cx('disableDialog')}>
                <I18n path="insurance.disableDialog" />
                <div className={cx('disableDialogControls')}>
                  <Button
                    className="default"
                    onClick={this.disable}
                  >
                    <I18n path="common.confirm" />
                  </Button>
                  <Link
                    className={cx('disableDialogLink')}
                    onClick={this.toggleDisableDialog}
                  >
                    <I18n path="common.cancel" />
                  </Link>
                </div>
              </Status>
            </>
          )}

          {isDisabling && (
            <Status className={cx('disableDialog')}>
              <Preloader
                className={cx('preloader')}
                size="48"
              />
            </Status>
          )}

          {requestId && (
            <Status className={cx('status')}>
              <I18n path="insurance.disableSuccess" vars={{ id: requestId }} />
            </Status>
          )}

          {error && (
            <Status className={['error', cx('status')]} emoji="statusFail">
              {error.message}
            </Status>
          )}

          <div className={cx('contentRow')}>
            {shortDescription}
          </div>

          <div className={cx('contentRow')}>
            <I18n
              path="insurance.insuranceSum"
              vars={{ sum: insuranceSum.toLocaleString() }}
            />
          </div>

          <div className={cx('contentRow')}>
            <Link href={downloadUrl} target="_blank">
              <I18n
                path="insurance.policyLink"
                vars={{ number }}
              />
            </Link>
          </div>

          {lastPayedPeriodEnd && isFuture(lastPayedPeriodEnd) ? (
            <>
              {currentPeriod && currentPeriod.status === 'Paid' ? (
                <Status className={['success', 'compact', cx('status')]}>
                  <I18n
                    path="insurance.currentPeriodPaid"
                    vars={{ date: formatDate(lastPayedPeriodEnd) }}
                  />
                </Status>
              ) : (
                <Status className={['compact', cx('status')]}>
                  <I18n
                    path="insurance.onlyFuturePeriodPaid"
                    vars={{ date: formatDate(lastPayedPeriod.datePeriodStart) }}
                  />
                </Status>
              )}
            </>
          ) : (
            <Status className={['error', 'compact', cx('status')]}>
              <I18n path="insurance.noLastPayedPeriod" />
            </Status>
          )}

          <div className={cx('contentRow')}>
            <Link
              onClick={() => this.setState({
                isPeriodsTableVisible: !isPeriodsTableVisible,
              })}
            >
              <I18n path="insurance.periods.link" />
            </Link>
          </div>

          {isPeriodsTableVisible && (
            <div className={cx('periods')}>
              <Table
                data={periods}
                config={{
                  columns: [
                    {
                      name: 'insurance.periods.dates',
                      dataField: 'datePeriodStart',
                      transformer: (row) => `
                        ${formatDateString(row.datePeriodStart, 'DD.MM.YYYY')}
                        —
                        ${formatDateString(row.datePeriodEnd, 'DD.MM.YYYY')}
                      `,
                    },
                    {
                      name: 'insurance.periods.status',
                      dataField: 'status',
                      transformer: (row) => (row.status === 'Paid' ? 'Оплачен' : 'Не оплачен'),
                    },
                  ],
                  uniqueIdField: 'datePeriodStart',
                }}
              />
            </div>
          )}

          {!isEnabled && (
            <Status
              className={['attention', cx('status')]}
              emoji="statusFail"
            >
              <I18n
                path="insurance.disabledService"
                vars={{ date: formatDate(lastPayedPeriodEnd) }}
              />
              <div className={cx('statusButton')}>
                <Button
                  href={presetUrl}
                  className="default"
                >
                  К подключению услуги
                </Button>
              </div>
            </Status>
          )}
        </div>

        <div className={cx('price')}>
          {isEnabled
            ? `${subscriptionFee || 0} ₽ в месяц`
            : 'Услуга отключена'}
        </div>
      </div>
    );
  }
}

InsurancePolicy.propTypes = {
  ...policeShape,
};
